import React from "react";
import { useT } from "@racoon/i18n";
import { ConnectProto } from "@racoon/api";
import { ShiftsCascade } from "./ShiftsCascade";
import type { ShiftGroup } from "../helpers/shiftGroups";
import type { ShiftData } from "../model/shift-data";
import type { ShiftsView } from "../model/use-shifts-view";
import { useSelectedCompany } from "~/shared/store/company/hooks/useSelectedCompany";
import {
  useGetOrganizationSettingsQuery,
  useGetOrganizationByIdQuery,
} from "~/shared/api/organization/organization.grpc";
import { printWaybills, type ShiftData as PrintShiftData } from "../helpers/print-waybill.helper";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useShiftApproval } from "../hooks/useShiftApproval";

interface ShiftsTabProps {
  /**
   * The registry's shifts in the list's own order. Approval searches these for a
   * conflicting running shift and takes the first match, so the order must not
   * follow the view.
   */
  shifts: ShiftData[];
  /** The same shifts, grouped the way `view` shows them. */
  groups: ShiftGroup[];
  view: ShiftsView;
  /** Active fleet — trucks lead the cascade and carry its header data. */
  trucks: ConnectProto.Truck[];
  /** Assignable drivers, for planning a shift straight from a group header. */
  drivers?: { id: string; name: string }[];
  /** Day a shift planned from here defaults to — the start of the viewed range. */
  defaultDate?: Date;
  isLoading: boolean;
  refetch: () => Promise<any>;
}

export const ShiftsTab: React.FC<ShiftsTabProps> = ({
  shifts,
  groups,
  view,
  trucks,
  drivers,
  defaultDate,
  isLoading,
  refetch,
}) => {
  const t = useT();

  // Fetch organization data
  const { selectedCompany: organization, selectedCompanyId } = useSelectedCompany();
  const { activeCompanyId } = useAuthSession();

  // Fetch current organization settings (only when needed)
  const { data: organizationSettings, refetch: refetchSettings } = useGetOrganizationSettingsQuery(
    {},
    {
      skip: !activeCompanyId,
    },
  );

  // Fetch current organization data
  const { data: organizationData } = useGetOrganizationByIdQuery(
    { organizationId: BigInt(selectedCompanyId!) },
    { skip: !selectedCompanyId },
  );

  const getShifts = React.useCallback(() => {
    return shifts.map((s) => ({
      // Rows carry the id as a decimal string; BigInt keeps every digit of an int64.
      id: BigInt(s.id),
      status: s.status,
      truckId: s.truckId,
      driverId: s.driverId,
      driverName: s.driverName,
      plateNumber: s.plateNumber,
      releasedAt: s.releasedAt,
      startedAt: s.startedAt,
      twStartAt: s.twStartAt,
      createdAt: s.createdAt,
    }));
  }, [shifts]);

  const {
    approveShift: handleApproveShift,
    completeShift: handleCompleteShift,
    isCompletingShift,
    isShiftBusy,
  } = useShiftApproval({
    selectedCompanyId,
    getShifts,
    refetch,
  });

  // Handler: print a single waybill
  const handlePrintSingleWaybill = async (shift: any) => {
    // Refresh organization settings before printing
    const { data: freshSettings } = await refetchSettings();

    const printData: PrintShiftData[] = [
      {
        id: shift.id,
        status: shift.status,
        plateNumber: shift.plateNumber,
        driverName: shift.driverName,
        type: shift.type,
        model: shift.model,
        trailer: shift.trailer,
        releasedAt: shift.releasedAt,
        twStartAt: shift.twStartAt,
        mileageBefore: shift.mileageBefore,
        acceptedAt: shift.acceptedAt,
        completed_at: shift.completed_at,
      },
    ];

    await printWaybills(printData, {
      organization: organizationData || organization || undefined,
      organizationSettings: freshSettings || organizationSettings || undefined,
      t,
    });
  };

  // A row carries only its truck id, so its cells look the vehicle up here.
  const trucksById = React.useMemo(
    () => new Map(trucks.map((truck) => [String(truck.id), truck])),
    [trucks],
  );

  const driverChange = React.useMemo(
    () => ({
      drivers: drivers ?? [],
      defaultDate,
      onShiftChanged: refetch,
    }),
    [drivers, defaultDate, refetch],
  );

  return (
    <div className="w-full min-w-0 flex flex-col min-h-0 flex-1 gap-4">
      <ShiftsCascade
        // A fresh table per view: column order, visibility and widths are set from
        // the view's own saved layout on mount, and group keys of one view (truck
        // ids) must not collapse groups of the other (driver ids).
        key={view}
        groups={groups}
        view={view}
        trucksById={trucksById}
        loading={isLoading}
        driverChange={driverChange}
        onCompleteShift={handleCompleteShift}
        onApproveShift={handleApproveShift}
        onPrintWaybill={handlePrintSingleWaybill}
        isCompletingShift={isCompletingShift}
        isShiftBusy={isShiftBusy}
      />
    </div>
  );
};
